import clsx from 'clsx'
import React, { PropsWithChildren, ReactNode } from 'react'
import { AnalyticsElement } from '@typesApp/Analytics'
import { SVGIcon } from '@components/foundation/svg-icon/SVGIcon'
import { injectAnalytics } from '@utils/analyticsAttributes'
import { injectDataAttributes } from '@utils/dataAttributes'
import styles from './styles/Drawer.module.scss'

type BaseProps = PropsWithChildren & {
  className?: string
}

type DrawerProps = BaseProps & {
  open?: boolean
  hideCloseButton?: boolean
  topHeader?: ReactNode
  onClose?: () => void
}

export const Drawer: React.FC<DrawerProps> = ({ open, onClose, hideCloseButton, topHeader, className, children }) => {
  const vh = document.documentElement.clientHeight * 0.01

  const dataAttributes = {
    ...injectDataAttributes('app.drawer'),
    ...(open ? injectDataAttributes('app.drawerOpen') : undefined),
  }

  return (
    <div className={clsx(styles.backdrop, open && styles.backdropOpen)} style={{ '--vh': `${vh}px` }}>
      <div className={clsx(styles.drawer, open && styles.drawerOpen, className)} {...dataAttributes}>
        <div className={styles.content}>
          {(!hideCloseButton || topHeader) && (
            <div className={clsx(styles.topHeader, topHeader && styles.withContent)}>
              {topHeader}
              {!hideCloseButton && (
                <button
                  className={styles.closeButton}
                  onClick={onClose}
                  {...injectDataAttributes('app.drawerClose')}
                  {...injectAnalytics(AnalyticsElement.CLOSE)}
                >
                  <SVGIcon name="close" />
                </button>
              )}
            </div>
          )}
          {open && children}
        </div>
      </div>
    </div>
  )
}

type DrawerContentHeaderProps = BaseProps & {
  title?: ReactNode
  description?: ReactNode
  noShadow?: boolean
}

export const DrawerContentHeader: React.FC<DrawerContentHeaderProps> = ({
  className,
  title,
  description,
  children,
  noShadow,
}) => {
  return (
    <div
      className={clsx(
        styles.contentHeader,
        title && !description && styles.titleOnly,
        noShadow && styles.noShadow,
        className
      )}
      {...injectDataAttributes('app.drawerHeader')}
    >
      {typeof title === 'string' ? <h2 className={styles.headerTitle}>{title}</h2> : title}
      {typeof description === 'string' ? <p className={styles.headerDescription}>{description}</p> : description}
      {children}
    </div>
  )
}

type DrawerContentBodyProps = BaseProps & {
  center?: boolean
}

export const DrawerContentBody: React.FC<DrawerContentBodyProps> = ({ className, children, center }) => {
  return (
    <div
      className={clsx(styles.contentBody, center && styles.center, className)}
      {...injectDataAttributes('app.drawerContent')}
    >
      {children}
    </div>
  )
}

type DrawerContentFooterProps = BaseProps & {
  noGutter?: boolean
}

export const DrawerContentFooter: React.FC<DrawerContentFooterProps> = ({ noGutter, className, children }) => {
  return (
    <div
      className={clsx(styles.contentFooter, noGutter && styles.noGutter, className)}
      {...injectDataAttributes('app.drawerFooter')}
    >
      {children}
    </div>
  )
}
